import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getSlrBookingDetailsResponse, updateSlrStatus, submitSlrUpdate } from '../../../services/slrService'
import { slrBookingDetailsSuccess, slrBookingDetailsError, clearSlrBookingResult } from '../../../redux/actions/slrBookingDetailsActions'
import { getAirportsByCode } from '../../../services/searchPanelServices'
import LoadingIndicator from '../../LoadingIndicator'
import SLRCargoDetails from './SLRCargoDetails'
import SlrPrintTemplate from './SlrPrintTemplate'
import { openSlrPrintPreview } from './slrPdfUtils'
import '../../ManageBooking/ViewBooking/index.scss'
import './SlrBookingDetailsView.scss'
import { useHistory } from 'react-router-dom'
import MESSAGES from '../../../constants/Messages'
import { Alert } from '../../../common/Alert'
import { useTranslation } from 'react-i18next'
import leftArrow from '../../../assets/images/left-arrow.png'
import { getDisplayStatus, getStatusLabelKey, isEnterpriseOrGsaUser } from '../SLRUtils'
import { CustomPopup } from '../../../common/Popup/CustomPopup'
import { CommonDropDown } from '../../../common/Resusable/CommonDropDown'
import AirportAutocomplete from '../../AirportAutocomplete/AirportAutocomplete'
import CommonInput from '../../../common/Resusable/CommonInput'
import { AUTOCOMPLETE_TYPE } from '../../../constants'
import plusIcon from '../../../assets/images/plusBlue.svg'
import deleteIcon from '../../../assets/images/delete_icon_red.png'
import checkCircleIcon from '../../../assets/images/check-circle-filled.svg'
import closeCircleIcon from '../../../assets/images/close-circle-filled-red.svg'
import alertIcon from '../../../assets/images/errorFilledOrange.svg'

const SlrBookingDetailsView = ({ slrNumber, customerCode }) => {
	//for multilingual
	const { t, i18n } = useTranslation(['labels', 'errors'])
	const isRTL = i18n.language === 'ar' || i18n.dir() === 'rtl'
	const [isEnterpriseOrGsa] = useState(() => isEnterpriseOrGsaUser())
	const dispatch = useDispatch()
	const { resultData } = useSelector(state => state.slrBookingDetails)
	const [bookingDetails, setBookingDetails] = useState(null)
	const printableRef = useRef(null)
	const printTemplateRef = useRef(null)
	const pdfInProgressRef = useRef(false)
	const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const history = useHistory()
	const [displayChangeStatusPopup, setDisplayChangeStatusPopup] = useState(false)
	const [selectedSlrStatus, setSelectedSlrStatus] = useState(null)
	const [initialSlrStatus, setInitialSlrStatus] = useState(null)
	const [slrStatusError, setSlrStatusError] = useState(null)
	const [rejectionRemarks, setRejectionRemarks] = useState('')
	const [rejectionRemarksError, setRejectionRemarksError] = useState(null)
	const [changeStatusAlert, setChangeStatusAlert] = useState(null)
	const [cargoRouteRows, setCargoRouteRows] = useState([])
	const [initialCargoRouteRows, setInitialCargoRouteRows] = useState(null)
	const transitAirportsKeyRef = useRef(null)
	const [routeRowErrors, setRouteRowErrors] = useState({})

	const hasTransitChanges = initialCargoRouteRows !== null && (
		cargoRouteRows.length !== initialCargoRouteRows.length ||
		cargoRouteRows.some((row, index) => {
			const initialRow = initialCargoRouteRows[index]
			return row.airport?.code !== initialRow.airport?.code ||
				(row.carrier || '') !== (initialRow.carrier || '')
		})
	)

	const addRouteRow = () => setCargoRouteRows(prev => [...prev, { id: Date.now(), airport: null, carrier: '' }])

	const removeRouteRow = (id) => {
		setCargoRouteRows(prev => prev.filter(r => r.id !== id))
		setRouteRowErrors(prev => {
			const updated = { ...prev }
			delete updated[id]
			return updated
		})
	}

	const updateRouteRow = (id, field, value) => {
		setCargoRouteRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r))
		setRouteRowErrors(prev => {
			if (!prev[id]?.[field]) return prev
			const updated = { ...prev, [id]: { ...prev[id] } }
			delete updated[id][field]
			if (Object.keys(updated[id]).length === 0) delete updated[id]
			return updated
		})
	}

	const handleSubmit = async () => {
		if (!hasTransitChanges || isLoading) return

		// Validate cargo route rows
		if (cargoRouteRows.length > 0) {
			const errors = {}
			cargoRouteRows.forEach(row => {
				const rowErr = {}
				if (!row.airport?.code) rowErr.airport = true
				if (!row.carrier?.trim()) rowErr.carrier = true
				if (Object.keys(rowErr).length > 0) errors[row.id] = rowErr
			})
			if (Object.keys(errors).length > 0) {
				setRouteRowErrors(errors)
				return
			}
		}

		// Build transit airports from cargoRouteRows
		const transitAirports = cargoRouteRows.map(row => ({
			airportCode: row.airport?.code,
			carrierCode: row.carrier,
		}))

		// Transform dimensionDetails from response format to request format
		// Maps response field names/structure to API request expectations
		const transformedDimensionDetails = (bookingDetails.dimensionDetails || []).map(dim => ({
			pieces: dim.pieces,
			length: dim.length,
			width: dim.width,
			height: dim.height,
			dimensionUnit: dim.dimensionUnit,
			weightPerPiece: dim.weightPerPiece,
			weightPerPieceUnit: dim.weightUnit || 'K',
			nonStackable: !(dim.stackable ?? true),
			nonTiltable: !(dim.tiltable ?? true),
			nonCraneLiftable: !(dim.craneLiftable ?? true),
			nonForkLiftable: !(dim.forkLiftable ?? true),
			fragile: dim.fragile,
			packingType: dim.packingType,
		}))


		const sccCodes = bookingDetails.cargoDetails?.sccCodes || []
		const primarySccValue = sccCodes[0] || null
		const specialHandlingCodeValue = sccCodes.length > 0
			? sccCodes.join(', ')
			: null

		// Build SLR update payload
		const slrUpdatePayload = {
			origin: bookingDetails.origin,
			destination: bookingDetails.destination,
			shippingDate: bookingDetails.shippingDate,
			product: bookingDetails.cargoDetails?.product,
			commodity: bookingDetails.cargoDetails?.commodity,
			primaryScc: primarySccValue,
			specialHandlingCode: specialHandlingCodeValue,
			grossWeight: bookingDetails.cargoDetails?.grossWeight,
			grossWeightUnit: bookingDetails.cargoDetails?.weightUnit,
			totalPieces: bookingDetails.dimensionDetails?.reduce((sum, dim) => sum + (dim.pieces || 0), 0) || 0,
			stackable: bookingDetails.dimensionDetails?.[0]?.stackable ?? true,
			tiltable: bookingDetails.dimensionDetails?.[0]?.tiltable ?? true,
			craneLiftable: bookingDetails.dimensionDetails?.[0]?.craneLiftable ?? true,
			forkLiftable: bookingDetails.dimensionDetails?.[0]?.forkLiftable ?? true,
			fragile: bookingDetails.dimensionDetails?.[0]?.fragile ?? false,
			customerCode: bookingDetails.customerCode,
			customerName: bookingDetails.agentName,
			awbNumber: bookingDetails.cargoDetails?.awbNumber,
			shipmentDescription: bookingDetails.cargoDetails?.shipmentDescription,
			dimensionDetails: transformedDimensionDetails,
			attachments: bookingDetails?.attachments || [],
			submittedBy: bookingDetails.submittedBy,
			transitAirports,
			skyPalletRequest: bookingDetails.skyPalletDetails,
		}

		if (!bookingDetails?.slrId) {
			setChangeStatusAlert({
				message: t('BOOKING.SLR_UPDATE_FAILURE', { ns: 'labels' }),
				isError: true,
			})
			return
		}
		setIsLoading(true)
		try {
			const result = await submitSlrUpdate(bookingDetails.slrId, slrUpdatePayload)

			if (result?.status === 200) {
				// Rebuild transitAirports from the current resolved rows (already in memory)
				// so the useEffect guard doesn't trigger a redundant airport API re-fetch
				const submittedTransitAirports = cargoRouteRows.map(row => ({
					airportCode: row.airport?.code,
					carrierCode: row.carrier,
				}))
				const mergedData = {
					...(result.data || bookingDetails),
					transitAirports: submittedTransitAirports,
				}
				// Sync the ref so the transit useEffect skips this bookingDetails change
				transitAirportsKeyRef.current = JSON.stringify(submittedTransitAirports)
				setBookingDetails(mergedData)
				setInitialCargoRouteRows(cargoRouteRows)
				setChangeStatusAlert({
					message: t('BOOKING.SLR_UPDATE_SUCCESS', { ns: 'labels' }),
					isError: false,
				})
			} else {
				setChangeStatusAlert({
					message: t('BOOKING.SLR_UPDATE_FAILURE', { ns: 'labels' }),
					isError: true,
				})
			}
		} catch (error) {
			console.error('[SlrBookingDetailsView] Failed to update SLR:', error)
			setChangeStatusAlert({
				message: t('BOOKING.SLR_UPDATE_FAILURE', { ns: 'labels' }),
				isError: true,
			})
		} finally {
			setIsLoading(false)
		}
	}

	const getCallerScreenUrl = () => {
		return '/slr-bookings'
	}

	const fetchSlrDetails = async () => {
		if (!slrNumber) return
		setIsLoading(true)
		try {
			const response = await getSlrBookingDetailsResponse(slrNumber, customerCode)

			// Check response status
			if (response.status !== 200) {
				console.error('[SlrBookingDetailsView] API returned non-200 status:', response.status)
				dispatch(slrBookingDetailsError('Failed to fetch SLR details'))
				history.push({
					pathname: getCallerScreenUrl(),
					state: { slrFetchFailedError: true },
				})
				return
			}

			const bookingDetailsRaw = response.data

			if (bookingDetailsRaw && bookingDetailsRaw.slrReferenceNumber) {
				if (bookingDetailsRaw.cargoDetails?.sccCodes) {
					bookingDetailsRaw.cargoDetails.sccCodes = [
						...new Set(
							bookingDetailsRaw.cargoDetails.sccCodes.filter(scc => scc)
						)
					]
				}

				const slrStatus = bookingDetailsRaw?.slrStatus || ''
				const rejectionReason = bookingDetailsRaw?.rejectionReason || ''
				const bookingDetailsWithSlrStatus = {
					...bookingDetailsRaw,
					slrStatus,
					rejectionReason,
				}

				dispatch(slrBookingDetailsSuccess(bookingDetailsWithSlrStatus))
				setBookingDetails(bookingDetailsWithSlrStatus)
				window.scrollTo(0, 0)
			} else {
				console.error('[SlrBookingDetailsView] Invalid SLR response structure')
				dispatch(slrBookingDetailsError('Invalid response structure'))
				history.push({
					pathname: getCallerScreenUrl(),
					state: { slrFetchFailedError: true },
				})
			}
		} catch (error) {
			console.error('[SlrBookingDetailsView] Failed to load SLR booking details:', error)
			dispatch(slrBookingDetailsError(error.message))
			history.push({
				pathname: getCallerScreenUrl(),
				state: { slrFetchFailedError: true },
			})
		} finally {
			setIsLoading(false)
		}
	}

	// Fetch SLR booking details by slrNumber
	useEffect(() => {
		fetchSlrDetails()
	}, [slrNumber, customerCode]) // eslint-disable-line react-hooks/exhaustive-deps

	// Show booking submission result from redux, then clear it so it doesn't reappear on refresh
	useEffect(() => {
		if (resultData) {
			const message = resultData.success
				? t('BOOKING.SLR_UPDATE_SUCCESS')
				: t('BOOKING.SLR_UPDATE_FAILURE')
			setChangeStatusAlert({ message, isError: !resultData.success })
			dispatch(clearSlrBookingResult())
		}
	}, [resultData]) // eslint-disable-line react-hooks/exhaustive-deps

	// Scroll to top when changeStatusAlert is set
	useEffect(() => {
		if (changeStatusAlert) {
			window.scrollTo(0, 0)
		}
	}, [changeStatusAlert])


	// Populate transit routes from API response
	// Guard: only re-populate when transitAirports actually changes (avoids overwriting user edits after handleSubmit)
	useEffect(() => {
		if (!bookingDetails) return
		const transitAirports = Array.isArray(bookingDetails.transitAirports)
			? bookingDetails.transitAirports
			: []
		const transitKey = JSON.stringify(transitAirports)
		if (transitAirportsKeyRef.current === transitKey) return
		transitAirportsKeyRef.current = transitKey
		if (transitAirports.length === 0) {
			setCargoRouteRows([])
			setInitialCargoRouteRows([])
			return
		}
		Promise.all(
			bookingDetails.transitAirports.map(transit =>
				getAirportsByCode(transit.airportCode)
					.catch(error => {
						console.error(`[SlrBookingDetailsView] Failed to fetch airport ${transit.airportCode}:`, error)
						return { code: transit.airportCode, name: transit.airportCode }
					})
			)
		)
			.then(resolvedAirports => {
				const routes = resolvedAirports.map((airport, index) => ({
					id: Date.now() + Math.random(),
					airport: airport,
					carrier: bookingDetails.transitAirports[index].carrierCode || ''
				}))
				setCargoRouteRows(routes)
				setInitialCargoRouteRows(routes)
			})
			.catch(error => {
				console.error('[SlrBookingDetailsView] Failed to populate transit routes:', error)
				const routes = bookingDetails.transitAirports.map((transit) => ({
					id: Date.now() + Math.random(),
					airport: { code: transit.airportCode, name: transit.airportCode },
					carrier: transit.carrierCode || ''
				}))
				setCargoRouteRows(routes)
				setInitialCargoRouteRows(routes)
			})
	}, [bookingDetails])



	const handleBack = () => {
		history.push({
			pathname: getCallerScreenUrl(),
			state: { retainFormData: true },
		})
		window.scrollTo(0, 0)
	}

	const handleModifySlr = () => {
		if (!bookingDetails || Object.keys(bookingDetails).length === 0) {
			return
		}
		dispatch(slrBookingDetailsSuccess(bookingDetails))
		history.push(
			`/slr-bookings/modify/${bookingDetails.slrReferenceNumber}`,
			{ customerCode: bookingDetails.customerCode || customerCode }
		)
	}

	const printDetails = async () => {
		if (pdfInProgressRef.current || !printTemplateRef.current) return
		pdfInProgressRef.current = true
		setIsGeneratingPdf(true)
		try {
			await openSlrPrintPreview(printTemplateRef.current, `SLR-${slrNumber}.pdf`)
		} catch (error) {
			setChangeStatusAlert({
				isError: true,
				message: t(MESSAGES.PROCESS_REQUEST_FAILED, { ns: 'errors' }),
			})
		} finally {
			pdfInProgressRef.current = false
			setIsGeneratingPdf(false)
		}
	}

	const allowedSlrStatuses = ['N', 'C', 'P', 'R']

	const slrStatusOptions = allowedSlrStatuses.map((code) => ({
		label: t(getStatusLabelKey(code)),
		value: code,
	}))

	const openChangeStatusPopup = () => {
		const currentStatus = bookingDetails?.slrStatus
		setSelectedSlrStatus(currentStatus)
		setInitialSlrStatus(currentStatus)
		setSlrStatusError(null)
		setRejectionRemarks(bookingDetails?.rejectionReason || '')
		setRejectionRemarksError(null)
		setDisplayChangeStatusPopup(true)
	}

	const closeChangeStatusPopup = () => {
		setSlrStatusError(null)
		setRejectionRemarks('')
		setRejectionRemarksError(null)
		setDisplayChangeStatusPopup(false)
	}

	const onChangeSlrStatus = async () => {
		if (!selectedSlrStatus) return
		const hasRejectionReasonChanged = selectedSlrStatus === 'R' &&
			rejectionRemarks.trim() !== (bookingDetails?.rejectionReason || '').trim()
		if (selectedSlrStatus === initialSlrStatus && !hasRejectionReasonChanged) {
			setSlrStatusError(t('ER295', { ns: 'errors' }))
			return
		}
		if (!bookingDetails.slrId) {
			setSlrStatusError(t('BOOKING.SLR_STATUS_UPDATE_FAILURE', { ns: 'labels' }))
			return
		}
		if (selectedSlrStatus === 'R' && !rejectionRemarks.trim()) {
			setRejectionRemarksError(t('ER296', { ns: 'errors' }))
			return
		}
		setSlrStatusError(null)
		setRejectionRemarksError(null)
		setDisplayChangeStatusPopup(false)
		setIsLoading(true)
		try {
			const result = await updateSlrStatus(
				selectedSlrStatus,
				customerCode,
				bookingDetails.slrId,
				selectedSlrStatus === 'R' ? rejectionRemarks.trim() : undefined
			)
			if (result.status === 200) {
				setChangeStatusAlert({ message: t('BOOKING.SLR_STATUS_UPDATE_SUCCESS', { ns: 'labels' }), isError: false })
				if (result?.data?.slrStatus) {
					setBookingDetails(prev => ({
						...prev,
						slrStatus: result.data.slrStatus,
						rejectionReason: selectedSlrStatus === 'R' ? rejectionRemarks : '',
					}))
				} else {
					await fetchSlrDetails()
				}
			} else {
				setChangeStatusAlert({ message: t('BOOKING.SLR_STATUS_UPDATE_FAILURE', { ns: 'labels' }), isError: true })
			}
		} catch (error) {
			console.error('[SlrBookingDetailsView] Failed to update SLR status:', error)
			setChangeStatusAlert({ message: t('BOOKING.SLR_STATUS_UPDATE_FAILURE', { ns: 'labels' }), isError: true })
		} finally {
			setIsLoading(false)
		}
	}

	// English status is passed to the PDF template, which stays untranslated
	const slrDisplayStatus = getDisplayStatus(
		bookingDetails?.slrStatus
	)
	const slrStatusLabel = t(getStatusLabelKey(bookingDetails?.slrStatus))

	return (
		<div id='bookingDetails' className={`booking-details slr-booking-details ${isRTL ? 'rtl' : ''}`}>
			{isLoading ? <LoadingIndicator /> : <></>}
			{changeStatusAlert && (
				<Alert
					isError={changeStatusAlert.isError}
					showAlert={true}
					message={changeStatusAlert.message}
					setTimeout={true}
					onAlertClose={() => setChangeStatusAlert(null)}
				/>
			)}

			<div className='booking-details-view-wrapper'>
				<div className='booking-view-heading-section'>
					<div className='form-row align-items-center mb-0'>
						<div className='col-12 col-lg-12'>
							<button
								type='button'
								className='icon-btn d-flex align-items-center manage-booking-back-btn'
								onClick={handleBack}
							>
								<img src={leftArrow} alt='Back' className='left-arrow-btn' />
								<span className='left-arrow-btn-text'>{t('COMMON.BACK_BUTTON')}</span>
							</button>
						</div>
					</div>

					<div className='form-row align-items-center'>
						<div className='col-12 col-lg-12 d-flex align-items-center justify-content-between'>
							<h3 className='page-heading slr-details-heading pl-0 pb-0 pt-0 mb-0 mt-0'>
								{t('BOOKING.SLR_DETAILS_HEADING')}
							</h3>
							{isEnterpriseOrGsa && (
								<div className='cargo-edit-link' onClick={handleModifySlr}>
									<span>{t('COMMON.MODIFY')}</span>
								</div>
							)}
						</div>
					</div>
				</div>

				{bookingDetails !== null ? (
					(
						<div className='view-booking-details-section booking-details slr-print-content' ref={printableRef}>
							<h3 className='page-heading slr-details-heading slr-print-only-heading pl-0 pb-0 pt-0 mb-0 mt-0'>
								{t('BOOKING.SLR_DETAILS_HEADING')}
							</h3>
							<div className='form-row primary-details-wrapper booking-details-card' id='primary-details-wrapper'>
								{/* Status row */}
								<div className='status-row slr-status-row'>
									<span className='slr-number-status'>
										<div className='main-label'>
											{t('BOOKING.SLR_REFERENCE_ID')} : {slrNumber}
										</div>
										<div className={`slr-status-pill ${bookingDetails?.slrStatus}`}>
											{slrStatusLabel}
										</div>
									</span>
									{isEnterpriseOrGsa && (
										<div className='cargo-edit-link' onClick={openChangeStatusPopup}>
											<span>{t('BOOKING.SLR_CHANGE_STATUS')}</span>
										</div>
									)}
								</div>

								{/* Details row */}
								<div className={`details-row slr-details-row ${bookingDetails.rejectionReason ? 'has-rejection-reason' : ''}`}>
									<div className='details-col'>
										<span className='details-heading d-none d-lg-inline'>
											{t('CHOOSE_FLT.ORGIN_DESTINATION')}
										</span>
										<span className={`details-heading d-inline d-lg-none ${i18n.language === 'es' ? 'details-heading--spanish-mobile' : ''}`}>
											{t('CHOOSE_FLT.ORGIN_DESTINATION_MOBILE')}
										</span>
										<span className='details-data'>
											{bookingDetails.origin && bookingDetails.destination
												? `${bookingDetails.origin} - ${bookingDetails.destination}`
												: ' '}
										</span>
									</div>
									<div className='details-separator'></div>
									<div className='details-col'>
										<span className={`details-heading ${i18n.language === 'es' ? 'details-heading--spanish-mobile' : ''}`}>
											{t('BOOKING.SLR_SUBMITTED_DATE')}
										</span>
										<span className='details-data'>
											{bookingDetails.submittedDate || ' '}
											{bookingDetails.submittedTime && (
												<span className='slr-utc-time'>{bookingDetails.submittedTime}</span>
											)}
										</span>
									</div>
									<div className='details-separator'></div>
									<div className='details-col'>
										<span className={`details-heading ${i18n.language === 'es' ? 'details-heading--spanish-mobile' : ''}`}>
											{t('COMMON.SHP_DATE')}
										</span>
										<span className='details-data'>
											{bookingDetails.shippingDate || ' '}
										</span>
									</div>

									<div className='details-separator'></div>
									<div className='details-col'>
										<span className={`details-heading ${i18n.language === 'es' ? 'details-heading--spanish-mobile' : ''}`}>
											{t('COMMON.CUS_CODE')}
										</span>
										<span className='details-data'>
											{bookingDetails.agentCode && bookingDetails.agentName
												? `${bookingDetails.agentCode} - ${bookingDetails.agentName}`
												: bookingDetails.agentCode || bookingDetails.agentName || ' '}
										</span>
									</div>
									{bookingDetails.rejectionReason && (
										<>
											<div className='details-separator slr-rejection-reason-separator'></div>
											<div className='details-col slr-rejection-reason-col'>
												<span className='details-heading'>
													{t('COMMON.REJECTION_REASON')}
												</span>
												<span className='details-data slr-rejection-reason-data'>
													{bookingDetails.rejectionReason}
												</span>
											</div>
										</>
									)}
								</div>
							</div>

							<div>
								<div className='d-flex align-items-center justify-content-between cargo-details-header'>
									<h3 className='page-sub-head-view-booking mb-1'>{t('COMMON.CARGO_DTLS')}</h3>
								</div>
								<SLRCargoDetails slrResponse={bookingDetails} />
							</div>

							{/* Routing Details, Other Details, Submitted By, Skypallet */}

							{(isEnterpriseOrGsa || (cargoRouteRows.length !== 0 && !isEnterpriseOrGsa)) &&
								<div>
									<h3 className='page-sub-head-view-booking mb-1'>{t('BOOKING.SLR_ROUTING_DETAILS')}</h3>
									<div className='cargo-details-card-custom'>
										{(
											cargoRouteRows.map((row) => (
												<div key={row.id} className={`cargo-route-row ${!isEnterpriseOrGsa ? 'responsive-cargo-row' : ''}`}>
													{isEnterpriseOrGsa ? <>
														<div className={`form-group common-autocomplete slr-autocomplete ${routeRowErrors[row.id]?.airport ? 'red-border' : ''}`}>
															<div className='p-float-label input-wrapper'>
																<AirportAutocomplete
																	autoCompleteType={AUTOCOMPLETE_TYPE.ORIGIN}
																	selectedOrigin={row.airport}
																	setSelectedOrigin={(airport) => updateRouteRow(row.id, 'airport', airport)}
																	selectedDestination={null}
																	setSelectedDestination={() => { }}
																	clearError={() => { }}
																	handleSetError={() => { }}
																	screen='MANAGE_BOOKING'
																	displaySimilarBooking={false}
																	draftCallback={() => { }}
																	setNotLeave={() => { }}
																/>
																<label>{t('COMMON.AIRPORT_CODE')}</label>
															</div>
															<span className='search-panel__errormsg'>
																{routeRowErrors[row.id]?.airport ? t('ER291', { ns: 'errors' }) : ''}
															</span>
														</div>
														<div className='form-group'>
															<CommonInput
																type='text'
																label={t('CAPTURE_AWB.CARRIER_CODE')}
																value={row.carrier}
																onChange={(e) => updateRouteRow(row.id, 'carrier', e.target.value.toUpperCase())}
																error={routeRowErrors[row.id]?.carrier ? t('ER148', { ns: "errors" }) : ''}
																disabled={!isEnterpriseOrGsa}
															/>
														</div>
													</> :
														<>
															<div className='cargo-details-col'>
																<div className='cargo-details-heading'>{t('COMMON.AIRPORT_CODE')}</div>
																<div className='cargo-details-data'>
																	{row.airport?.name}, {row.airport?.code}
																</div>
															</div>
															<div className='cargo-details-col'>
																<div className='cargo-details-heading'>{t('CAPTURE_AWB.CARRIER_CODE')}</div>
																<div className='cargo-details-data'>{row.carrier || '--'}</div>
															</div>

														</>}
													{isEnterpriseOrGsa && (
														<div
															className='cargo-row-remove-btn'
															onClick={() => removeRouteRow(row.id)}
														>
															<img src={deleteIcon} alt='Trash' title='Trash' />
														</div>
													)}
												</div>
											))
										)}
										{isEnterpriseOrGsa && (
											<div className='add-more-btn mt-1' onClick={addRouteRow}>
												<img className='add-more-plus' src={plusIcon} alt='plus' title='add-more-plus' />
												<span className='add-more-link'>{t('BOOKING.SLR_ADD_TRANSIT')}</span>
											</div>
										)}
									</div>
								</div>}
							{bookingDetails.submittedBy && (
								<div>
									<h3 className='page-sub-head-view-booking mb-1'>{t('BOOKING.SLR_SUBMITTED_BY')}</h3>
									<div className='cargo-details-card-custom'>
										<div className='cargo-details-row responsive-cargo-row'>
											<div className='cargo-details-col'>
												<div className='cargo-details-heading'>{t('COMMON.NAME')}</div>
												<div className='cargo-details-data'>{bookingDetails.submittedBy.name || '--'}</div>
											</div>
											<div className='cargo-details-col'>
												<div className='cargo-details-heading'>{t('COMMON.EMAIL')}</div>
												<div className='cargo-details-data'>{bookingDetails.submittedBy.email || '--'}</div>
											</div>
											<div className='cargo-details-col'>
												<div className='cargo-details-heading'>{t('COMMON.TEL_NO')}</div>
												<div className='cargo-details-data'>{bookingDetails.submittedBy.phoneNumber || '--'}</div>
											</div>
										</div>
									</div>
								</div>
							)}

							{isEnterpriseOrGsa && (
								<div>
									<h3 className='page-sub-head-view-booking mb-1'>{t('BOOKING.SLR_SKYPALLET_RESPONSE')}</h3>
									<div className='cargo-details-card-custom slr-skypallet-card'>
										{!bookingDetails?.skyPalletDetails ? (
											<div className='slr-not-available-message ml-0 mr-0'><img src={alertIcon} alt='Not Loadable' className='slr-status-icon' />{t('BOOKING.SLR_NOT_YET_RECEIVED')}</div>
										) : (
											<>
												{/* Loadability Details - Full width row */}
												<div className='cargo-details-row responsive-cargo-row'>
													<div className='cargo-details-col'>
														<div className='cargo-details-heading'>{t('COMMON.STATUS')}</div>
														<div className='cargo-details-data slr-loadable-status-container'>
															{(() => {
																const statusConfig = {
																	LOADABLE: { label: 'Loadable', icon: checkCircleIcon },
																	SPECIAL_LOAD: { label: 'Special Loadable', icon: alertIcon },
																	NOT_LOADABLE: { label: 'Not Loadable', icon: closeCircleIcon },
																}[bookingDetails?.skyPalletDetails?.loadableStatus]
																return statusConfig ? (
																	<>
																		<span>{statusConfig.label}</span>
																		<img src={statusConfig.icon} alt={statusConfig.label} className='slr-status-icon' />
																	</>
																) : '--'
															})()}
														</div>
													</div>
													{bookingDetails?.skyPalletDetails?.adjustedWeight != null && (
														<div className='cargo-details-col'>
															<div className='cargo-details-heading'>{t('BOOKING.SLR_ADJUSTED_WEIGHT')}</div>
															<div className='cargo-details-data'>{bookingDetails.skyPalletDetails.adjustedWeight} Kg</div>
														</div>
													)}
													{bookingDetails?.skyPalletDetails?.loadableVolume != null && (
														<div className='cargo-details-col'>
															<div className='cargo-details-heading'>{t('BOOKING.SLR_LOADABLE_VOLUME')}</div>
															<div className='cargo-details-data'>{bookingDetails.skyPalletDetails.loadableVolume} CBM</div>
														</div>
													)}
												</div>

												{/* ULD details - Second row */}
												{bookingDetails?.skyPalletDetails?.ulds?.length > 0 && (
													<div className='slr-skypallet-uld-section mt-3'>
														<h3 className='loose-details-head mb-1'>{t('BOOKING.SLR_ULDS')}</h3>
														<div className='looseInfo-card-wrapper slr-booking'>
															{bookingDetails.skyPalletDetails.ulds.map((uld, index) => (
																<div className='looseInfo-card' key={index}>
																	<div className='looseInfo-row'>
																		<div className='looseInfo-col'>
																			<div className='looseInfo-heading'>{t('BOOKING.SLR_ULD_POSITION')}</div>
																			<div className='looseInfo-data'>{uld.uld || '-'}</div>
																		</div>
																		<div className='looseInfo-col'>
																			<div className='looseInfo-heading'>{t('BOOKING.SLR_ULD_COUNT')}</div>
																			<div className='looseInfo-data'>{uld.uldCount ?? '-'}</div>
																		</div>
																	</div>
																</div>
															))}
														</div>
													</div>
												)}

												{/* Visual Representation Link - Second row */}
												{bookingDetails?.skyPalletDetails?.visualRepresentationLink && (
													<div className='cargo-details-row responsive-cargo-row slr-visual-representation-row'>
														<div className='cargo-details-col' >
															<a
																href={bookingDetails.skyPalletDetails.visualRepresentationLink}
																target='_blank'
																rel='noopener noreferrer'
																className='add-more-link'
															>
																{t('BOOKING.SLR_SEE_VISUAL_REPRESENTATION')} →
															</a>
														</div>
													</div>
												)}
											</>
										)}
									</div>
								</div>
							)}


							<div className='form-row cancel-button-row'>
								<div className='footer-button'>
									<button
										type='button'
										className='secondary-button'
										onClick={() => history.push('/slr-bookings')}
									>
										{t('BOOKING.SLR_VIEW_LIST')}
									</button>
								</div>
								<div className='cancel-button-actions'>
									{isEnterpriseOrGsa && (
										<button type='button' className='secondary-button' onClick={handleSubmit} disabled={!hasTransitChanges || isLoading}>
											{t('E_AWB.AGRMNT_SUBMIT_BUTTON')}
										</button>
									)}
									<button type='button' className='primary-button' onClick={printDetails} disabled={isGeneratingPdf} aria-busy={isGeneratingPdf}>
										{t('BOOKING.SLR_DOWNLOAD_PDF')}
									</button>
								</div>
							</div>
						</div>
					)
				) : (
					<div className='no-data ml-0 mr-0'>
						{t('BOOKING.SLR_DETAILS_NOT_AVAILABLE')}
					</div>
				)}
			</div>

			<SlrPrintTemplate
				ref={printTemplateRef}
				bookingDetails={bookingDetails}
				slrDisplayStatus={slrDisplayStatus}
				isEnterpriseOrGsa={isEnterpriseOrGsa}
			/>


			{displayChangeStatusPopup && (
				<CustomPopup
					isVisible={displayChangeStatusPopup}
					onClose={closeChangeStatusPopup}
					headerComponent={{
						mainHeader: t('BOOKING.SLR_CHANGE_STATUS_POPUP_HEADER'),
					}}
					footerSecBtnLabel={t('COMMON.CANCEL')}
					footerSecBtnClick={closeChangeStatusPopup}
					footerPriBtnLabel={t('BOOKING.SLR_CHANGE_STATUS_BTN')}
					footerPriBtnClick={onChangeSlrStatus}
					footerPriBtnDisabled={!selectedSlrStatus}
					body={<div className='slr-status-change-popup-body'>
						<div className='cargo-details-row responsive-cargo-row'>
							<div className='cargo-details-col'>
								<div className='cargo-details-heading'>{t('BOOKING.SLR_CURRENT_STATUS')}</div>
								<div className='cargo-details-data'>{slrStatusLabel || '-'}</div>
							</div>
							<div className='cargo-details-col'>
								<CommonDropDown
									className='slr-status-dropdown'
									value={selectedSlrStatus}
									options={slrStatusOptions}
									label={t('BOOKING.SLR_CHANGE_STATUS_TO')}
									onChange={(e) => { setSelectedSlrStatus(e.value); setSlrStatusError(null); setRejectionRemarks(''); setRejectionRemarksError(null) }}
									appendTo={document.body}
									panelClassName='slr-status-dropdown-panel'
									error={slrStatusError}
								/>
							</div>
						</div>

						{selectedSlrStatus === 'R' && (
							<div className='cargo-details-row responsive-cargo-row'>
								<div className='cargo-details-col'>
									<div className='cargo-details-heading slr-rejection-reason-wrapper'>
										<label className='cargo-details-heading'>{t('BOOKING.SLR_REJECTION_REASON')}</label>
										<textarea
											className={`slr-rejection-reason-textarea ${rejectionRemarksError ? 'error' : ''}`}
											value={rejectionRemarks}
											onChange={(e) => { setRejectionRemarks(e.target.value); setRejectionRemarksError(null) }}
											rows={2}
										/>
										{rejectionRemarksError && (
											<span className='slr-rejection-reason-error'>{rejectionRemarksError}</span>
										)}
									</div>
								</div>
							</div>
						)}
					</div>}
				/>
			)}


		</div>
	)
}

export default SlrBookingDetailsView
